import { logger } from '../../services/logger';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../db';

export interface CurrencySettings {
  code: string;
  symbol: string;
  name: string;
  decimalPlaces: number;
  symbolPosition: 'before' | 'after';
}

export interface SystemSettings {
  currency: CurrencySettings;
  serviceFee: number; // Service fee percentage (0.08 = 8%)
  affiliateFeePercentage: number; // Affiliate fee as percentage of service fee (0.125 = 12.5%)
  creatorFeePercentage: number; // Creator fee as percentage of service fee (0.125 = 12.5%)
  // Add other settings groups here in the future
}

// Database schema types (matches the JSON structure in Postgres)
interface DbCurrencySettings {
  code: string;
  symbol: string;
  name: string;
  decimal_places: number;
  symbol_position: 'before' | 'after';
}

interface DbSettings {
  currency: DbCurrencySettings;
  serviceFee: number;
  affiliateFeePercentage: number;
  creatorFeePercentage: number;
}

interface SettingsContextType {
  settings: SystemSettings;
  loading: boolean;
  error: string | null;
  refreshSettings: () => Promise<void>;
  formatCurrency: (amount: number, decimalPlaces?: number) => string;
}

// Default settings with ZAR (South African Rand)
const defaultSettings: SystemSettings = {
  currency: {
    code: 'ZAR',
    symbol: 'R',
    name: 'South African Rand',
    decimalPlaces: 2,
    symbolPosition: 'before',
  },
  serviceFee: 0.08, // 8% default service fee
  affiliateFeePercentage: 0.125, // 12.5% of service fee (1% of bet when service fee is 8%)
  creatorFeePercentage: 0.125, // 12.5% of service fee (1% of bet when service fee is 8%)
};

const defaultContextValue: SettingsContextType = {
  settings: defaultSettings,
  loading: true,
  error: null,
  refreshSettings: async () => {},
  formatCurrency: (amount: number, decimalPlaces?: number) => `R${amount.toFixed(decimalPlaces ?? defaultSettings.currency.decimalPlaces)}`,
};

const SettingsContext = createContext<SettingsContextType>(defaultContextValue);

export const useSettings = () => useContext(SettingsContext);

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<SystemSettings>(defaultSettings);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState<boolean>(false);

  // Transform database settings to our TypeScript structure
  const transformDbSettings = (dbSettings: Partial<DbSettings>): SystemSettings => {
    const newSettings = { ...defaultSettings };
    
    // Transform currency settings
    if (dbSettings.currency) {
      newSettings.currency = {
        code: dbSettings.currency.code || defaultSettings.currency.code,
        symbol: dbSettings.currency.symbol || defaultSettings.currency.symbol,
        name: dbSettings.currency.name || defaultSettings.currency.name,
        decimalPlaces: dbSettings.currency.decimal_places || defaultSettings.currency.decimalPlaces,
        symbolPosition: dbSettings.currency.symbol_position || defaultSettings.currency.symbolPosition,
      };
    }
    
    // Transform service fee setting
    if (dbSettings.serviceFee !== undefined) {
      newSettings.serviceFee = dbSettings.serviceFee;
    }
    
    // Transform affiliate fee percentage setting
    if (dbSettings.affiliateFeePercentage !== undefined) {
      newSettings.affiliateFeePercentage = dbSettings.affiliateFeePercentage;
    }
    
    // Transform creator fee percentage setting
    if (dbSettings.creatorFeePercentage !== undefined) {
      newSettings.creatorFeePercentage = dbSettings.creatorFeePercentage;
    }
    
    return newSettings;
  };

  // Fetch settings from the database
  const refreshSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch currency settings
      const { data: currencyData, error: currencyError } = await supabase.rpc(
        'get_setting',
        { setting_key: 'currency' }
      );
      
      if (currencyError) throw currencyError;
      
      // Fetch service fee setting
      const { data: serviceFeeData, error: serviceFeeError } = await supabase.rpc(
        'get_setting',
        { setting_key: 'service_fee' }
      );
      
      if (serviceFeeError) throw serviceFeeError;

      // Fetch affiliate fee percentage setting
      const { data: affiliateFeeData, error: affiliateFeeError } = await supabase.rpc(
        'get_setting',
        { setting_key: 'affiliate_fee_percentage' }
      );
      
      if (affiliateFeeError) throw affiliateFeeError;

      // Fetch creator fee percentage setting
      const { data: creatorFeeData, error: creatorFeeError } = await supabase.rpc(
        'get_setting',
        { setting_key: 'creator_fee_percentage' }
      );
      
      if (creatorFeeError) throw creatorFeeError;
      
      // Apply settings
      const settings: Partial<DbSettings> = {};
      if (currencyData) {
        settings.currency = currencyData;
      }
      if (serviceFeeData !== null) {
        settings.serviceFee = Number(serviceFeeData);
      }
      if (affiliateFeeData !== null) {
        settings.affiliateFeePercentage = Number(affiliateFeeData);
      }
      if (creatorFeeData !== null) {
        settings.creatorFeePercentage = Number(creatorFeeData);
      }
      
      setSettings(transformDbSettings(settings));
      setInitialized(true);
    } catch (err) {
      logger.error('Error fetching settings:', err);
      setError('Failed to load system settings');
    } finally {
      setLoading(false);
    }
  };

  // Format currency using current settings
  const formatCurrency = (amount: number, decimalPlaces?: number): string => {
    if (!settings) return amount.toString();
    
    const formatter = new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: settings.currency.code,
      minimumFractionDigits: decimalPlaces ?? settings.currency.decimalPlaces,
      maximumFractionDigits: decimalPlaces ?? settings.currency.decimalPlaces,
      useGrouping: true
    });
    
    return formatter.format(amount);
  };
  
  // Load settings on initial render
  useEffect(() => {
    if (!initialized) {
      refreshSettings();
    }
  }, [initialized]);

  return (
    <SettingsContext.Provider
      value={{
        settings,
        loading,
        error,
        refreshSettings,
        formatCurrency,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
} 